import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  MoreVertical,
  Plus,
  Search,
  Pencil,
  KeyRound,
  Mail,
  User,
  Phone,
  AtSign,
} from "lucide-react";

import { apiFetch } from "@/lib/api";
import { UserForm, Option } from "./dialog/UserForm";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { UserMenu } from "@/components/Holizontal/UserMenu";

type UserRow = {
  id: number;
  firstname: string;
  lastname: string;
  username: string;
  email: string;
  mobile: string;
  status: number;
  BRANCH?: string | null;
  ROLE?: string | null;
  role_id: number;
  branch_id: number;
};

type CursorMeta = {
  per_page?: number;
  next_cursor: string | null;
  prev_cursor?: string | null;
  has_more: boolean;
};

type CursorResponse<T> = {
  data: T[];
  meta: CursorMeta;
  anchor_id?: number | null;
};

type ApiListResponse<T> = { data: T[] };

function clsx(...s: Array<string | false | undefined | null>) {
  return s.filter(Boolean).join(" ");
}

function useDebouncedValue<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/** Infinite-scroll sentinel (uses viewport root to prevent “flipping”) */
function useInfiniteSentinel(opts: { enabled: boolean; onLoadMore: () => void }) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!opts.enabled) return;
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) opts.onLoadMore();
      },
      { root: null, rootMargin: "520px", threshold: 0.01 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [opts.enabled, opts.onLoadMore]);

  return sentinelRef;
}

function StatusPill({ status }: { status: number }) {
  const active = status === 1;
  return (
    <span
      className={clsx(
        "inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border",
        active
          ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/20"
          : "bg-rose-500/15 text-rose-600 border-rose-500/20"
      )}
    >
      {active ? "Active" : "Suspended"}
    </span>
  );
}

function SkeletonRow({ rtl }: { rtl: boolean }) {
  return (
    <tr className="border-b">
      <td className="py-4 px-3 text-muted-foreground">—</td>
      <td className="py-4 px-3"><div className="h-4 w-28 rounded bg-muted animate-pulse" /></td>
      <td className="py-4 px-3"><div className="h-4 w-28 rounded bg-muted animate-pulse" /></td>
      <td className="py-4 px-3"><div className="h-4 w-64 rounded bg-muted animate-pulse" /></td>
      <td className="py-4 px-3"><div className="h-6 w-24 rounded bg-muted animate-pulse" /></td>
      <td className={clsx("py-4 px-3", rtl ? "text-left" : "text-right")}>
        <div className="inline-block h-8 w-10 rounded bg-muted animate-pulse" />
      </td>
    </tr>
  );
}

function UserCard({
  u,
  rtl,
  onEdit,
  onReset,
  t,
}: {
  u: UserRow;
  rtl: boolean;
  onEdit: () => void;
  onReset: () => void;
  t: any;
}) {
  return (
    <Card className="rounded-2xl shadow-card border overflow-hidden">
      <CardContent className="p-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="rounded-xl border bg-muted/20 p-2 shrink-0">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>

              <div className="min-w-0">
                <div className="font-semibold truncate">
                  {u.firstname} {u.lastname}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="rounded-full">
                    {u.BRANCH || "—"}
                  </Badge>
                  <Badge variant="outline" className="rounded-full">
                    {u.ROLE || "—"}
                  </Badge>
                   <StatusPill status={u.status} />
                </div>
              </div>
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-2">
           
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-xl">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={rtl ? "start" : "end"} className="rounded-xl">
                <DropdownMenuItem onClick={onEdit}>
                  <Pencil className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onReset}>
                  <KeyRound className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                  Reset Password
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Separator className="my-3" />

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <AtSign className="h-4 w-4" />
            <span className="truncate">{u.username ?? "-"}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="h-4 w-4" />
            <span className="truncate">{u.mobile ?? "-"}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mail className="h-4 w-4" />
            <span className="truncate">{u.email ?? "-"}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function UsersPage() {
  const qc = useQueryClient();

  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const rtl = dir === "rtl";
  const { t } = useTranslation();

  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);
  const [perPage, setPerPage] = useState(25);

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);

  // Branch search (shared)
  const [branchSearch, setBranchSearch] = useState("");
  const branchSearchDebounced = useDebouncedValue(branchSearch, 300);

  // Create form
  const [createForm, setCreateForm] = useState({
    branch: "" as string | number,
    role: "" as string | number,
    firstname: "",
    lastname: "",
    username: "",
    email: "",
    mobile: "",
  });

  // Edit form
  const [editForm, setEditForm] = useState({
    id: null as number | null,
    branch: "" as string | number,
    role: "" as string | number,
    firstname: "",
    lastname: "",
    username: "",
    email: "",
    mobile: "",
  });

  // Reset password form
  const [pwdForm, setPwdForm] = useState({ password: "", password_confirmation: "" });

  // Branches
  const branchesQ = useQuery({
    queryKey: ["branches_options", branchSearchDebounced],
    queryFn: async () => {
      const res = await apiFetch<ApiListResponse<Option>>(
        `/users/branches?q=${encodeURIComponent(branchSearchDebounced)}`
      );
      return res.data;
    },
    staleTime: 60_000,
    retry: 1,
  });

  // Roles
  const rolesQ = useQuery({
    queryKey: ["roles_options"],
    queryFn: async () => {
      const res = await apiFetch<ApiListResponse<Option>>("/users/roles");
      return res.data;
    },
    staleTime: 60_000,
    retry: 1,
  });

  // Infinite users cursor query
  const usersInf = useInfiniteQuery({
    queryKey: ["users_cursor", qDebounced, perPage],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("per_page", String(perPage));
      if (pageParam) params.set("cursor", String(pageParam));
      if (qDebounced?.trim()) params.set("q", qDebounced.trim());
      return apiFetch<CursorResponse<UserRow>>(`/users?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => (lastPage?.meta?.has_more ? lastPage.meta.next_cursor : null),
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });

  const pages = usersInf.data?.pages ?? [];
  const users = useMemo(() => pages.flatMap((p) => p.data || []), [pages]);
  const lastMeta = pages.length ? pages[pages.length - 1]?.meta : undefined;

  const loadingFirst = usersInf.isLoading;
  const loadingMore = usersInf.isFetchingNextPage;

  const sentinelRef = useInfiniteSentinel({
    enabled: !!usersInf.hasNextPage && !usersInf.isFetchingNextPage && !usersInf.isLoading,
    onLoadMore: () => {
      if (usersInf.hasNextPage && !usersInf.isFetchingNextPage) usersInf.fetchNextPage();
    },
  });

  // Create user
  const createMut = useMutation({
    mutationFn: async (payload: any) => apiFetch<UserRow>("/users", { method: "POST", body: payload }),

    onMutate: async (newUser) => {
      await qc.cancelQueries({ queryKey: ["users_cursor"] });

      const previous = qc.getQueryData<any>(["users_cursor", qDebounced, perPage]);

      qc.setQueryData<any>(["users_cursor", qDebounced, perPage], (old: any) => {
        if (!old?.pages?.length) return old;
        const firstPage = old.pages[0];
        return {
          ...old,
          pages: [
            {
              ...firstPage,
              data: [
                {
                  id: Date.now(),
                  ...newUser,
                  status: 1,
                  BRANCH: "",
                  ROLE: "",
                },
                ...firstPage.data,
              ],
            },
            ...old.pages.slice(1),
          ],
        };
      });

      return { previous };
    },

    onError: (_err, _newUser, ctx) => {
      if (ctx?.previous) qc.setQueryData(["users_cursor", qDebounced, perPage], ctx.previous);
      toast.error("Failed to create user");
    },

    onSuccess: () => {
      toast.success("User created");
      setCreateOpen(false);
    },

    onSettled: () => qc.invalidateQueries({ queryKey: ["users_cursor"] }),
  });

  // Edit user
  const editMut = useMutation({
    mutationFn: async (payload: any) =>
      apiFetch(`/users/${payload.id}`, {
        method: "PUT",
        body: payload,
      }),

    onMutate: async (updated) => {
      await qc.cancelQueries({ queryKey: ["users_cursor"] });
      const previous = qc.getQueryData<any>(["users_cursor", qDebounced, perPage]);

      qc.setQueryData<any>(["users_cursor", qDebounced, perPage], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((p: any) => ({
            ...p,
            data: p.data.map((u: UserRow) => (u.id === updated.id ? { ...u, ...updated } : u)),
          })),
        };
      });

      return { previous };
    },

    onError: (_err, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(["users_cursor", qDebounced, perPage], ctx.previous);
      toast.error("Update failed");
    },

    onSuccess: () => {
      toast.success("User updated");
      setEditOpen(false);
    },

    onSettled: () => qc.invalidateQueries({ queryKey: ["users_cursor"] }),
  });

  // Reset password
  const resetMut = useMutation({
    mutationFn: async (payload: any) => {
      if (!selectedUser?.id) throw new Error("No user selected");
      return apiFetch(`/users/${selectedUser.id}/password`, { method: "POST", body: payload });
    },
    onSuccess: () => {
      toast.success("Password reset successful");
      setResetOpen(false);
      setPwdForm({ password: "", password_confirmation: "" });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to reset password"),
  });

  const openCreate = () => {
    setBranchSearch("");
    setCreateOpen(true);
  };

  const openEdit = (u: UserRow) => {
    setBranchSearch("");
    setEditForm({
      id: u.id,
      branch: String(u.branch_id ?? ""),
      role: String(u.role_id ?? ""),
      firstname: u.firstname,
      lastname: u.lastname,
      username: u.username,
      email: u.email,
      mobile: u.mobile,
    });
    setEditOpen(true);
  };

  const openReset = (u: UserRow) => {
    setSelectedUser(u);
    setPwdForm({ password: "", password_confirmation: "" });
    setResetOpen(true);
  };

  const submitCreate = () => {
    const payload = {
      branch: createForm.branch || null,
      role: createForm.role,
      firstname: createForm.firstname,
      lastname: createForm.lastname,
      username: createForm.username,
      email: createForm.email,
      mobile: createForm.mobile,
    };

    if (!payload.role) return toast.error("Role is required");
    if (!payload.firstname?.trim()) return toast.error("First name is required");
    if (!payload.username?.trim()) return toast.error("Username is required");

    createMut.mutate(payload);
  };

  const submitEdit = () => {
    const payload = {
      id: editForm.id,
      branch: editForm.branch || null,
      role: editForm.role,
      firstname: editForm.firstname,
      lastname: editForm.lastname,
      email: editForm.email,
      mobile: editForm.mobile,
    };

    if (!payload.id) return toast.error("Missing user id");
    if (!payload.role) return toast.error("Role is required");
    if (!payload.firstname?.trim()) return toast.error("First name is required");

    editMut.mutate(payload);
  };

  const submitReset = () => {
    if (!pwdForm.password) return toast.error("Password is required");
    if (pwdForm.password !== pwdForm.password_confirmation) return toast.error("Passwords do not match");
    resetMut.mutate(pwdForm);
  };

  return (
    <div dir={dir} className="w-full max-w-full overflow-x-hidden space-y-5">
      {/* Header (like Home: clean) */}
      <div className="flex items-center justify-between gap-3">
        <motion.h1
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold text-foreground"
        >
          Users
        </motion.h1>

        {/* small + professional button */}
        <Button
          onClick={openCreate}
          size="sm"
          className="rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-[0_10px_30px_rgba(124,58,237,0.18)]"
          disabled={loadingFirst}
        >
          <Plus className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
          <span className="hidden sm:inline">Create</span>
        </Button>
      </div>

      {usersInf.isError ? (
        <Card className="shadow-card">
          <CardContent className="p-4 text-sm text-red-600">
            {(usersInf.error as any)?.message || "Failed to load users"}
          </CardContent>
        </Card>
      ) : null}

      <UserMenu />
       <Card className="shadow-card overflow-hidden">
<CardHeader className="border-b border-border/60">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-lg">All Users</CardTitle>
              <Badge variant="outline" className="rounded-full text-xs">
                Loaded: <span className="ml-1 font-semibold">{users.length}</span>
              </Badge>
            </div>

            {/* Search + per page in same row (mobile too) */}
            <div className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-8 sm:col-span-9 relative min-w-0">
                <Search
                  className={clsx(
                    "absolute top-2.5 h-4 w-4 text-muted-foreground",
                    rtl ? "right-3" : "left-3"
                  )}
                />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search users..."
                  className={clsx("w-full rounded-xl", rtl ? "pr-10" : "pl-10")}
                />
              </div>

              <div className="col-span-4 sm:col-span-3">
                <select
                  className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      Per: {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </CardHeader>
       </Card>

      <Card className="shadow-card overflow-hidden">
        

        

        <CardContent className="p-0">
          {/* ✅ Mobile layout = cards (professional like Home) */}
          <div className="p-2 space-y-3 md:hidden">
            {loadingFirst ? (
              <>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Card key={i} className="rounded-2xl border shadow-card">
                    <CardContent className="p-4 space-y-2">
                      <div className="h-4 w-40 rounded bg-muted animate-pulse" />
                      <div className="h-4 w-28 rounded bg-muted animate-pulse" />
                      <div className="h-4 w-56 rounded bg-muted animate-pulse" />
                    </CardContent>
                  </Card>
                ))}
              </>
            ) : users.length === 0 ? (
              <Card className="rounded-2xl border shadow-card">
                <CardContent className="p-8 text-center text-muted-foreground">No data available</CardContent>
              </Card>
            ) : (
              <>
                {users.map((u) => (
                  <motion.div key={u.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    <UserCard
                      u={u}
                      rtl={rtl}
                      t={t}
                      onEdit={() => openEdit(u)}
                      onReset={() => openReset(u)}
                    />
                  </motion.div>
                ))}

                {loadingMore ? (
                  <div className="py-3 text-center text-sm text-muted-foreground">Loading more…</div>
                ) : null}

                <div ref={sentinelRef} />
              </>
            )}

            <div className="px-1 pb-3">
              <Button
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => usersInf.fetchNextPage()}
                disabled={!usersInf.hasNextPage || usersInf.isFetchingNextPage}
              >
                {usersInf.isFetchingNextPage ? "Loading..." : usersInf.hasNextPage ? "Load more" : "No more"}
              </Button>
            </div>
          </div>

          {/* ✅ Desktop/iPad = table (scroll only inside table, no layout break) */}
          <div className="hidden md:block">
            <div className="w-full max-w-full overflow-hidden">
              <div className="overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch]">
                <div className="min-w-[920px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
                      <tr className="text-left">
                        <th className="p-3 whitespace-nowrap">#</th>
                        <th className="p-3 whitespace-nowrap">{t("common.branch")}</th>
                        <th className="p-3 whitespace-nowrap">{t("common.role")}</th>
                        <th className="p-3 whitespace-nowrap">{t("common.name")}</th>
                        <th className="p-3 whitespace-nowrap">{t("common.status")}</th>
                        <th className={clsx("p-3 whitespace-nowrap", rtl ? "text-left" : "text-right")}>{t("common.action")}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loadingFirst ? (
                        <>
                          {Array.from({ length: 10 }).map((_, i) => (
                            <SkeletonRow key={`sk-${i}`} rtl={rtl} />
                          ))}
                        </>
                      ) : users.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="p-10 text-center text-muted-foreground">
                            No data available
                          </td>
                        </tr>
                      ) : (
                        users.map((u, idx) => (
                          <tr key={u.id} className="border-b hover:bg-muted/20">
                            <td className="p-3 text-muted-foreground">{idx + 1}</td>
                            <td className="p-3">{u.BRANCH || "—"}</td>
                            <td className="p-3">{u.ROLE || "—"}</td>

                            <td className="p-3">
                              <div className="flex items-start gap-2 min-w-0">
                                <User className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                                <div className="min-w-0">
                                  <div className="font-medium truncate">
                                    {u.firstname} {u.lastname}
                                  </div>

                                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                    <AtSign className="h-3.5 w-3.5 shrink-0" />
                                    <span className="truncate">{u.username ?? "-"}</span>
                                  </div>

                                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                    <Phone className="h-3.5 w-3.5 shrink-0" />
                                    <span className="truncate">{u.mobile ?? "-"}</span>
                                  </div>

                                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                    <Mail className="h-3.5 w-3.5 shrink-0" />
                                    <span className="truncate">{u.email ?? "-"}</span>
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="p-3">
                              <StatusPill status={u.status} />
                            </td>

                            <td className={clsx("p-3", rtl ? "text-left" : "text-right")}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="rounded-xl">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>

                                <DropdownMenuContent align={rtl ? "start" : "end"} className="rounded-xl">
                                  <DropdownMenuItem onClick={() => openEdit(u)}>
                                    <Pencil className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                                    {t("common.edit")}
                                  </DropdownMenuItem>

                                  <DropdownMenuItem onClick={() => openReset(u)}>
                                    <KeyRound className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                                    Reset Password
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))
                      )}

                      {loadingMore ? (
                        <>
                          {Array.from({ length: 6 }).map((_, i) => (
                            <SkeletonRow key={`more-${i}`} rtl={rtl} />
                          ))}
                        </>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div ref={sentinelRef} />

            <div className="flex items-center justify-between gap-3 border-t border-border/60 p-4">
              <div className="text-sm text-muted-foreground">
                Loaded <span className="font-medium text-foreground">{users.length}</span>{" "}
                {lastMeta?.has_more ? " (more available)" : " (end)"}
              </div>

              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => usersInf.fetchNextPage()}
                disabled={!usersInf.hasNextPage || usersInf.isFetchingNextPage}
              >
                {usersInf.isFetchingNextPage ? "Loading..." : usersInf.hasNextPage ? "Load more" : "No more"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* CREATE MODAL */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
          </DialogHeader>

          <UserForm
            form={createForm}
            setForm={setCreateForm}
            branches={branchesQ.data || []}
            roles={rolesQ.data || []}
            branchSearch={branchSearch}
            setBranchSearch={setBranchSearch}
            branchesLoading={branchesQ.isLoading}
            loading={createMut.isPending}
            onSubmit={submitCreate}
          />

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setCreateOpen(false)} disabled={createMut.isPending}>
              {t("common.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>

          <UserForm
            form={editForm}
            setForm={setEditForm}
            branches={branchesQ.data || []}
            roles={rolesQ.data || []}
            branchSearch={branchSearch}
            setBranchSearch={setBranchSearch}
            branchesLoading={branchesQ.isLoading}
            loading={editMut.isPending}
            isEdit
            onSubmit={submitEdit}
          />

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setEditOpen(false)} disabled={editMut.isPending}>
              {t("common.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RESET PASSWORD MODAL */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Reset Password {selectedUser ? `• ${selectedUser.username}` : ""}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">New Password</label>
              <Input
                className="rounded-xl"
                type="password"
                value={pwdForm.password}
                onChange={(e) => setPwdForm((s) => ({ ...s, password: e.target.value }))}
                placeholder="New Password"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Confirm Password</label>
              <Input
                className="rounded-xl"
                type="password"
                value={pwdForm.password_confirmation}
                onChange={(e) => setPwdForm((s) => ({ ...s, password_confirmation: e.target.value }))}
                placeholder="Confirm Password"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setResetOpen(false)} disabled={resetMut.isPending}>
              {t("common.cancel")}
            </Button>
            <Button className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitReset} disabled={resetMut.isPending}>
              {resetMut.isPending ? "Saving..." : "Reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}